import { createSlice } from '@reduxjs/toolkit';

import {
  requestFailedMatcher,
  resetLoadingOnRequestFailed,
} from '../async-state';
import {
  type IAdminCarrierPacket,
  type IImportPacketaCsvResult,
  type IPacketsState,
} from './types';

export const preloadedState: IPacketsState = {
  inProcess: false,
  confirmingPacketIds: [],
  packets: undefined,
  packet: undefined,
  orderPackets: undefined,
  importTotal: undefined,
  importCurrent: undefined,
  importResult: undefined,
  updatedAt: undefined,
};

const packetsSlice = createSlice({
  name: 'Packets',
  initialState: preloadedState,
  reducers: {
    packetsLoadAll(state) {
      state.inProcess = true;
      state.packets = undefined;
    },
    packetsLoadAllResponse: {
      reducer(state, action: { payload: { packets: IAdminCarrierPacket[] } }) {
        state.inProcess = false;
        state.packets = action.payload.packets;
        state.updatedAt = Date.now();
      },
      prepare(packets: IAdminCarrierPacket[]) {
        return { payload: { packets } };
      },
    },
    packetLoad: {
      reducer(state) {
        state.inProcess = true;
        state.packet = undefined;
      },
      prepare(packetId: number) {
        return { payload: { packet: { id: packetId } } };
      },
    },
    packetLoadResponse: {
      reducer(
        state,
        action: { payload: { packet: IAdminCarrierPacket | null } },
      ) {
        state.inProcess = false;
        state.packet = action.payload.packet;
        state.updatedAt = Date.now();
      },
      prepare(packet: IAdminCarrierPacket | null) {
        return { payload: { packet } };
      },
    },
    packetLoadByOrder: {
      reducer(state) {
        state.orderPackets = undefined;
      },
      prepare(orderUuid: string) {
        return { payload: { order: { uuid: orderUuid } } };
      },
    },
    packetLoadByOrderResponse: {
      reducer(
        state,
        action: { payload: { orderPackets: IAdminCarrierPacket[] } },
      ) {
        state.orderPackets = action.payload.orderPackets;
      },
      prepare(orderPackets: IAdminCarrierPacket[]) {
        return { payload: { orderPackets } };
      },
    },
    addManualPacket: {
      reducer(state) {
        state.inProcess = true;
      },
      prepare(
        orderUuid: string,
        carrierVendorCode: string,
        carrierServiceThirdPartyId: string,
        trackingNumber: string,
      ) {
        return {
          payload: {
            order: { uuid: orderUuid },
            carrierVendor: { code: carrierVendorCode },
            carrierService: { thirdPartyId: carrierServiceThirdPartyId },
            trackingNumber,
          },
        };
      },
    },
    addManualPacketResponse: {
      reducer(
        state,
        action: {
          payload: { success: boolean; packet: IAdminCarrierPacket | null };
        },
      ) {
        state.inProcess = false;
        if (action.payload.success && action.payload.packet) {
          state.orderPackets = [
            ...(state.orderPackets ?? []),
            action.payload.packet,
          ];
        }
      },
      prepare(success: boolean, packet: IAdminCarrierPacket | null) {
        return { payload: { success, packet } };
      },
    },
    importPacketaCsvStart: {
      reducer(state, action: { payload: { total: number } }) {
        state.inProcess = true;
        state.importTotal = action.payload.total;
        state.importCurrent = 0;
        state.importResult = undefined;
      },
      prepare(total: number) {
        return { payload: { total } };
      },
    },
    importPacketaCsv: {
      reducer(state) {
        state.inProcess = true;
      },
      prepare(csvContent: string) {
        return { payload: { csvContent } };
      },
    },
    importPacketaCsvResponse: {
      reducer(state, action: { payload: { result: IImportPacketaCsvResult } }) {
        const current = (state.importCurrent ?? 0) + 1;
        state.importCurrent = current;
        const next = action.payload.result;
        const prev = state.importResult;
        state.importResult = prev
          ? ({
              processed: prev.processed + next.processed,
              skipped: prev.skipped + next.skipped,
              errors: [...prev.errors, ...next.errors],
              pendingMatches: [...prev.pendingMatches, ...next.pendingMatches],
            } satisfies IImportPacketaCsvResult)
          : next;
        if (current >= (state.importTotal ?? 1)) {
          state.inProcess = false;
        }
      },
      prepare(result: IImportPacketaCsvResult) {
        return { payload: { result } };
      },
    },
    confirmBillingMatch: {
      reducer(
        state,
        action: {
          payload: {
            packetId: number;
            billedPrice: number;
            billedCurrency: string;
            billedDate: string;
          };
        },
      ) {
        state.confirmingPacketIds = [
          ...state.confirmingPacketIds,
          action.payload.packetId,
        ];
      },
      prepare(
        packetId: number,
        billedPrice: number,
        billedCurrency: string,
        billedDate: string,
      ) {
        return {
          payload: { packetId, billedPrice, billedCurrency, billedDate },
        };
      },
    },
    confirmBillingMatchResponse: {
      reducer(
        state,
        action: {
          payload: { packetId: number; success: boolean; error?: string };
        },
      ) {
        const { packetId, success } = action.payload;
        state.confirmingPacketIds = state.confirmingPacketIds.filter(
          (id) => id !== packetId,
        );
        if (success && state.importResult) {
          state.importResult = {
            ...state.importResult,
            pendingMatches: state.importResult.pendingMatches.filter(
              (m) => !m.availablePackets.some((p) => p.id === packetId),
            ),
          };
        }
      },
      prepare(packetId: number, success: boolean, error?: string) {
        return { payload: { packetId, success, error } };
      },
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      requestFailedMatcher('Packets'),
      resetLoadingOnRequestFailed,
    );
  },
});

export const {
  packetsLoadAll,
  packetsLoadAllResponse,
  packetLoad,
  packetLoadResponse,
  packetLoadByOrder,
  packetLoadByOrderResponse,
  addManualPacket,
  addManualPacketResponse,
  importPacketaCsvStart,
  importPacketaCsv,
  importPacketaCsvResponse,
  confirmBillingMatch,
  confirmBillingMatchResponse,
} = packetsSlice.actions;
export default packetsSlice.reducer;
